/**
 * Common date range filtering criteria.
 */
export interface IDateRangeCriteria {
  dateFrom?: string | number | null;
  dateTo?: string | number | null;
}

/**
 * Filter criteria specific to memories.
 */
export interface IMemoryFilterCriteria extends IDateRangeCriteria {
  searchQuery?: string;
  contactIds?: string[];
  groupIds?: string[];
  tagIds?: string[];
  mediaTypes?: ("all" | "image" | "video" | "audio" | "document" | "pdf" | "other")[];
  minRating?: number;
  memoryType?: string;
  onlyWithMedia?: boolean;
  placeIds?: string[];
  anchorIds?: string[];
}

/**
 * Filter criteria specific to media.
 */
export interface IMediaFilterCriteria extends IDateRangeCriteria {
  searchQuery?: string;
  mediaTypes?: ("all" | "image" | "video" | "audio" | "document" | "pdf" | "other")[];
  category?: string;
  minRating?: number;
  memoryIds?: string[];
  contactIds?: string[];
  groupIds?: string[];
  tagIds?: string[];
  placeIds?: string[];
  anchorIds?: string[];
}

/**
 * Filter criteria specific to contacts.
 */
export interface IContactFilterCriteria extends IDateRangeCriteria {
  searchQuery?: string;
  userRoles?: string[];
  isLinked?: boolean;
  groupIds?: string[];
  tagIds?: string[];
  contactIds?: string[];
  anchorIds?: string[];
}

/**
 * Filter criteria specific to groups.
 */
export interface IGroupFilterCriteria {
  searchQuery?: string;
  groupIds?: string[];
  contactIds?: string[];
  tagIds?: string[];
  minMemberCount?: number;
  anchorIds?: string[];
}

/**
 * Filter criteria specific to shares.
 */
export interface IShareFilterCriteria extends IDateRangeCriteria {
  searchQuery?: string;
  roles?: ("viewer" | "contributor" | "editor" | "admin")[];
  sourceType?: ("memory" | "media" | "contact" | "group" | "place" | "anchor");
  direction?: ("shared_by_me" | "shared_with_me" | "all");
  targetUserIds?: string[];
}

/**
 * Filter criteria specific to activity events.
 */
export interface IActivityFilterCriteria extends IDateRangeCriteria {
  searchQuery?: string;
  actions?: ("create" | "update" | "delete")[];
  entityTypes?: string[];
  actorUserIds?: string[];
}

/**
 * Filter criteria specific to messages.
 */
export interface IMessageFilterCriteria extends IDateRangeCriteria {
  searchQuery?: string;
  folder?: ("inbox" | "sent" | "trash" | "all");
  readStatus?: ("all" | "read" | "unread");
  contactIds?: string[];
}

/**
 * Filter criteria specific to tags.
 */
export interface ITagFilterCriteria {
  tagIds?: string[];
  tagTypes?: ("manual" | "location" | "date" | "sentiment" | "contact" | "group" | "media" | "memory")[];
}

/**
 * Filter criteria specific to places.
 */
export interface IPlaceFilterCriteria extends IDateRangeCriteria {
  searchQuery?: string;
  placeIds?: string[];
  minRating?: number;
  memoryIds?: string[];
  mediaIds?: string[];
  contactIds?: string[];
  groupIds?: string[];
  tagIds?: string[];
  anchorIds?: string[];
}

/**
 * Filter criteria specific to anchors.
 */
export interface IAnchorFilterCriteria extends IDateRangeCriteria {
  searchQuery?: string;
  query?: string;
  anchorIds?: string[];
  types?: ("animals" | "hobbies" | "courses" | "other")[];
  minRating?: number;
  memoryIds?: string[];
  mediaIds?: string[];
  contactIds?: string[];
  groupIds?: string[];
  placeIds?: string[];
  tagIds?: string[];
  hasLocation?: boolean;
  dateRange?: { from?: any; to?: any };
}

/**
 * Master Unified Filter Payload passed across client UI, persistence, and backend Get* functions.
 */
export interface IUnifiedFilter {
  searchQuery?: string;
  memoryFilter?: IMemoryFilterCriteria;
  mediaFilter?: IMediaFilterCriteria;
  contactFilter?: IContactFilterCriteria;
  groupFilter?: IGroupFilterCriteria;
  tagFilter?: ITagFilterCriteria;
  shareFilter?: IShareFilterCriteria;
  activityFilter?: IActivityFilterCriteria;
  messageFilter?: IMessageFilterCriteria;
  placeFilter?: IPlaceFilterCriteria;
  anchorFilter?: IAnchorFilterCriteria;
}

/**
 * Summary of filtered items returned when filter is collapsed or calculated.
 */
export interface IFilteredCountsSummary {
  total: number;
  memories: number;
  media: number;
  contacts: number;
  groups: number;
  places?: number;
  anchors?: number;
  shares?: number;
  activities?: number;
  messages?: number;
}
